import { Box, Checkbox, Dialog, DialogTitle, DialogContent, FormControlLabel, TextField } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { businessService, feedback, blockerService, ICreateBlocker } from '../../../services';
import { Api } from '../../../services/api/axios-config';
import { setCsrfRequestToken } from '../../../services/api/axios-config/interceptors';
import { Button } from '../../MUI/button/Button';
import { Dayjs } from 'dayjs';
import { DatePicker, EntitySelector, TimePicker } from '../../index';

interface ISchedulerCreateBlockerModalProps {
  open: boolean;
  businessId: number;
  businessEmployeeId?: number;
  onClose: () => void;
  refetch: () => void;
}

export const SchedulerCreateBlockerModal: React.FC<ISchedulerCreateBlockerModalProps> = ({
  open,
  businessId,
  businessEmployeeId,
  onClose,
  refetch,
}) => {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number>();
  const [isBusinessWide, setIsBusinessWide] = useState(false);
  const [reason, setReason] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([]);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [disableEndDate, setDisableEndDate] = useState<boolean>(false);
  const [noEnd, setNoEnd] = useState(false);
  const [weekly, setWeekly] = useState(false);

  const { data: business } = useQuery(
    ['business', businessId],
    () => businessService.getById(businessId)
  );

  const selectedEmployee = useMemo(
    () => business?.employees?.find(e => e.id === selectedEmployeeId),
    [business, selectedEmployeeId],
  );

  useEffect(() => {
    if (open) {
      setIsBusinessWide(false);
      setSelectedEmployeeId(undefined);
      setReason('');
      setStartDate(null);
      setStartTime(null);
      setEndTime(null);
      setEndDate(null);
      setDaysOfWeek([]);
      setNoEnd(false);
      setWeekly(false);
      setDisableEndDate(false);
    }
  }, [open]);

  useEffect(() => {
    if (businessEmployeeId && !isBusinessWide) {
      const emp = business?.employees?.find(e => e.id === businessEmployeeId);
      if (emp) {
        setSelectedEmployeeId(emp.id);
      }
    }
  }, [business, businessEmployeeId, isBusinessWide]);

  const { mutate: createBlocker, isLoading } = useMutation(
    async (blocker: ICreateBlocker) => {
      try {
        const tokenRes = await Api.get('/auth/antiforgery-token');
        if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
      } catch {
        // best-effort
      }
      return blockerService.create(blocker);
    },
    {
      onSuccess: () => {
        feedback('Bloqueio criado', 'success');
        onClose();
        refetch();
      },
      onError: (error: any) => feedback(String(error), 'error'),
    }
  );

  const handleNoEndChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNoEnd(event.target.checked);
    if (event.target.checked) {
      setEndDate(null);
      setDisableEndDate(true);
      return;
    }
    setDisableEndDate(false);
  };

  const handleWeeklyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWeekly(event.target.checked);
    setDaysOfWeek([]);
  };

  const moreThan7Days = useMemo(() => {
    return endDate && startDate && endDate.diff(startDate, 'day') >= 7;
  }, [startDate, endDate]);

  const canSubmit = (isBusinessWide || !!selectedEmployee?.id) && !!reason && !!startDate && (!noEnd && !!endDate || noEnd) && !!startTime && !!endTime && (!weekly || daysOfWeek.length > 0);

  const validateHoursAgainstSchedule = useCallback((): string | null => {
    // Business-wide blockers don't validate against a specific employee's schedule
    if (isBusinessWide) return null;

    if (!selectedEmployee?.weeklySchedules || selectedEmployee.weeklySchedules.length === 0)
      return null;

    const schedules = selectedEmployee.weeklySchedules;

    // Collect the days of week this blocker applies to
    const daysToCheck = new Set<number>();

    if (weekly && daysOfWeek.length > 0) {
      daysOfWeek.forEach(d => daysToCheck.add(d));
    } else if (startDate) {
      daysToCheck.add(startDate.day());
      if (endDate && !noEnd) {
        // Also check end date's day for multi-day blockers
        daysToCheck.add(endDate.day());
      }
    }

    if (daysToCheck.size === 0) return null;

    const dayNames = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

    for (const dayOfWeek of daysToCheck) {
      const schedule = schedules.find(s => s.dayOfWeek === dayOfWeek);
      if (!schedule || schedule.isClosed || !schedule.startTime || !schedule.endTime)
        continue;

      const schedStart = schedule.startTime.substring(0, 5);
      const schedEnd = schedule.endTime.substring(0, 5);
      const blkStart = startTime?.format('HH:mm');
      const blkEnd = endTime?.format('HH:mm');

      if (blkStart && blkStart < schedStart) {
        return `O horário de início (${blkStart}) é antes do funcionamento (${schedStart}) em ${dayNames[dayOfWeek]}.`;
      }

      if (blkEnd && blkEnd > schedEnd) {
        return `O horário de fim (${blkEnd}) é depois do funcionamento (${schedEnd}) em ${dayNames[dayOfWeek]}.`;
      }
    }

    return null;
  }, [selectedEmployee, startDate, endDate, noEnd, startTime, endTime, weekly, daysOfWeek]);

  const handleSubmit = useCallback(() => {
    if (!businessId || (!isBusinessWide && !selectedEmployee?.id) || !reason || !startDate || (!noEnd && !endDate) || !startTime || !endTime || (moreThan7Days && weekly && !daysOfWeek.length)) {
      feedback('Preencha todos os campos obrigatórios', 'error');
      return;
    }

    // Validate blocker hours against employee's weekly schedule (skipped for business-wide)
    const hourError = validateHoursAgainstSchedule();
    if (hourError) {
      feedback(hourError, 'error');
      return;
    }

    const now = new Date();

    if (startDate.isBefore(now)) {
      feedback('A data de início deve ser maior ou igual a hoje', 'error');
      return;
    }

    if (startDate.isSame(now, 'day') && startTime.isBefore(now)) {
      feedback('Não é possível criar um bloqueio para um horário passado.', 'error');
      return;
    }

    if (!noEnd && startDate.isAfter(endDate)) {
      feedback('A data de início deve ser menor ou igual à data de fim', 'error');
      return;
    }

    if (startTime.isAfter(endTime)) {
      feedback('O horário de início deve ser menor que o horário de fim', 'error');
      return;
    }

    const stringStartTime = startTime.format('HH:mm:ss');
    const stringEndTime = endTime.format('HH:mm:ss');

    createBlocker({
      startDate: startDate.toISOString(),
      endDate: endDate?.toISOString() || undefined,
      startTime: stringStartTime,
      endTime: stringEndTime,
      businessId,
      businessEmployeeId: isBusinessWide ? undefined : selectedEmployee!.id,
      daysOfWeek,
      reason,
    });
  }, [businessId, selectedEmployee, isBusinessWide, startDate, noEnd, endDate, startTime, endTime, moreThan7Days, weekly, daysOfWeek, reason]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Novo bloqueio</DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Estabelecimento"
            value={business?.name || ''}
            disabled
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isBusinessWide}
                onChange={(e) => {
                  setIsBusinessWide(e.target.checked);
                  if (e.target.checked) setSelectedEmployeeId(undefined);
                }}
              />
            }
            label="Bloqueio para todos os profissionais"
          />

          {!isBusinessWide && (
            <EntitySelector
              options={business?.employees?.map(e => ({ id: e.id, name: e.name })) || []}
              value={selectedEmployeeId}
              onChange={setSelectedEmployeeId}
              label="Profissional"
            />
          )}

          {isBusinessWide && (
            <EntitySelector
              options={business?.employees?.map(e => ({ id: e.id, name: e.name })) || []}
              value={selectedEmployeeId}
              onChange={setSelectedEmployeeId}
              label="Profissional"
              disabled
            />
          )}

          <TextField
            label="Motivo"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />

          <DatePicker
            label="Data de início"
            value={startDate}
            disablePast
            onChange={setStartDate}
          />

          <Box display="flex" gap={2}>
            <TimePicker
              label="Hora início"
              value={startTime}
              onChange={setStartTime}
            />

            <TimePicker
              label="Hora fim"
              value={endTime}
              onChange={setEndTime}
            />
          </Box>

          <Box display="flex" gap={2}>
            <Box display="flex" justifyContent="center" sx={{ width: '200px' }}>
              <FormControlLabel
                control={<Checkbox checked={noEnd} onChange={handleNoEndChange} />}
                label="Sem término"
              />
            </Box>

            <DatePicker
              label="Data de término"
              value={endDate}
              onChange={setEndDate}
              disabled={disableEndDate}
            />
          </Box>

          {moreThan7Days && (
            <>
              <FormControlLabel
                control={<Checkbox checked={weekly} onChange={handleWeeklyChange} />}
                label="Apenas em alguns dias da semana"
              />

              {weekly && (
                <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap">
                  {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((d, i) => (
                    <Button
                      disabled={!weekly}
                      key={i}
                      label={d}
                      borderRounded={false}
                      variant={daysOfWeek.includes(i) ? 'contained' : 'outlined'}
                      onClick={() =>
                        setDaysOfWeek(prev =>
                          prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]
                        )
                      }
                    />
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button label="Cancelar" variant="outlined" onClick={onClose} />
        <Button
          label="Salvar"
          variant="contained"
          onClick={handleSubmit}
          isLoading={isLoading}
          disabled={!canSubmit}
        />
      </Box>
    </Dialog>
  );
};
